import React, { useMemo } from 'react';
import { Table, Button, Spin, Empty, Tooltip } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { withRouter } from 'react-router';
import moment from 'moment';
import {
	formatDuration,
	countCompletedWorkOrders,
	partitionWorkOrders,
	parseLocationEvents,
	collectScheduleEvents,
	filterEventsByLocation,
} from '../../utils/WrenchModeEventUtils';
import { changeFilterValueToArrayOfIds, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { FILTER_CONFIG_NAMES } from '../../utils/DataConstants';
import { WrenchModeLogsFilters } from './WrenchModeLogsHeader';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';
import { workOrderSearchAndAPIAdaptor } from '../../thunks/work_orders_thunks_wrapper';

interface FlattenedRow {
	key: string;
	scheduleId: number;
	date: string;
	supplierEmail: string;
	locationName: string;
	locationId: number;
	workOrdersCompleted: number;
	workOrdersTotal: number;
	workOrders: any[];
	driveTimeMs: number;
	workTimeMs: number;
}

interface DateGroup {
	date: string;
	rows: FlattenedRow[];
	techSpanMap: Record<string, number>;
}

interface WrenchModeLogsTableProps {
	schedules: any[];
	// Technician contacts keyed by email, hydrated by the analytics endpoint. The schedules
	// payload carries only supplierEmail, so passing these through saves UserNameWithEmail a
	// lookup per technician. A tech missing from the map still falls back to that lookup.
	contactsByEmail?: Record<string, any>;
	loading: boolean;
	loadingMore: boolean;
	hasMore: boolean;
	onLoadMore: () => void;
	filters: WrenchModeLogsFilters;
	history?: any;
}

const buildTechSpanMap = (rows: FlattenedRow[]): Record<string, number> => {
	const spanMap: Record<string, number> = {};

	for (let i = 0; i < rows.length; ) {
		const currentEmail = rows[i].supplierEmail;
		let groupEnd = i + 1;

		while (groupEnd < rows.length && rows[groupEnd].supplierEmail === currentEmail) {
			groupEnd++;
		}

		spanMap[rows[i].key] = groupEnd - i;

		for (let j = i + 1; j < groupEnd; j++) {
			spanMap[rows[j].key] = 0;
		}

		i = groupEnd;
	}

	return spanMap;
};

const WrenchModeLogsTable: React.FC<WrenchModeLogsTableProps> = ({
	schedules,
	contactsByEmail = {},
	loading,
	loadingMore,
	hasMore,
	onLoadMore,
	filters,
	history,
}) => {
	const locationIdFilter = useMemo(
		() => changeFilterValueToArrayOfIds(filters[FILTER_CONFIG_NAMES.LOCATION]),
		[filters]
	);

	const groups = useMemo<DateGroup[]>(() => {
		const byDate: Record<string, FlattenedRow[]> = {};
		const dateOrder: string[] = [];

		for (const schedule of schedules) {
			const scheduleDate: string = schedule.date;
			const locations = schedule.locations || [];
			const allEvents = collectScheduleEvents(schedule);

			const hasLocationFilter = locationIdFilter.length > 0;
			const filteredLocations = hasLocationFilter
				? locations.filter((loc: any) => locationIdFilter.indexOf(loc.locationId) !== -1)
				: locations;

			if (filteredLocations.length === 0 && hasLocationFilter) continue;

			const locsToRender = filteredLocations.length > 0 ? filteredLocations : [null];

			for (let i = 0; i < locsToRender.length; i++) {
				const loc = locsToRender[i];

				const locationName = loc
					? nullSafeGetOrElse('location.name', loc, 'Unknown Location')
					: 'No location';

				const locationId = loc ? loc.locationId : 0;
				const workOrderIds = loc ? loc.workOrderIds || [] : [];
				const rawWorkOrders = (loc && loc.workOrders) || [];
				const adaptedWorkOrders = workOrderSearchAndAPIAdaptor(
					rawWorkOrders.map((wo) => ({ source: wo }))
				);
				const workOrdersCompleted = countCompletedWorkOrders(
					adaptedWorkOrders,
					schedule.supplierEmail,
					schedule.date
				);

				let driveTimeMs = 0;
				let workTimeMs = 0;

				if (loc) {
					const locEvents = filterEventsByLocation(allEvents, loc.locationId);

					if (locEvents.length > 0) {
						const locParsed = parseLocationEvents(locEvents);
						driveTimeMs = locParsed.driveTimeMs;
						workTimeMs = locParsed.totalWorkMs;
					}
				}

				const row: FlattenedRow = {
					key: schedule.id + '-' + i,
					scheduleId: schedule.id,
					date: scheduleDate,
					supplierEmail: schedule.supplierEmail,
					locationName,
					locationId,
					workOrdersCompleted,
					workOrdersTotal: workOrderIds.length,
					workOrders: adaptedWorkOrders,
					driveTimeMs,
					workTimeMs,
				};

				if (!byDate[scheduleDate]) {
					byDate[scheduleDate] = [];
					dateOrder.push(scheduleDate);
				}
				byDate[scheduleDate].push(row);
			}
		}

		return dateOrder.map((date) => {
			const rows = byDate[date];
			return { date, rows, techSpanMap: buildTechSpanMap(rows) };
		});
	}, [schedules, locationIdFilter]);

	const handleViewDetails = (supplierEmail: string, date: string, locationId: number) => {
		if (history) {
			const detailPath =
				'/supplier/fieldTechs/detail/' +
				encodeURIComponent(supplierEmail) +
				'/wrenchModeLogs?date=' +
				date +
				'&locationId=' +
				locationId;
			const linkWithBack = getLinkWIthBackLinkParams(
				history.location,
				'Back to Work Logs',
				detailPath
			);
			history.push(linkWithBack);
		}
	};

	const buildColumns = (techSpanMap: Record<string, number>) => [
		{
			title: 'Technician',
			dataIndex: 'supplierEmail',
			key: 'technician',
			width: '20%',
			render: (email: string, record: FlattenedRow) => {
				const span = techSpanMap[record.key];
				return {
					children: (
						<UserNameWithEmail
							showNameWithAvatar
							mode="inline"
							contact={contactsByEmail[email]}
							email={email}
						/>
					),
					props: { rowSpan: span },
				};
			},
		},
		{
			title: 'Location',
			dataIndex: 'locationName',
			key: 'locationName',
			width: '18%',
		},
		{
			title: 'Work Orders',
			key: 'workOrders',
			width: '14%',
			render: (_: any, record: FlattenedRow) => {
				const label = record.workOrdersCompleted + '/' + record.workOrdersTotal;
				if (record.workOrders.length === 0) return label;

				const { notStarted, started, ended } = partitionWorkOrders(
					record.workOrders,
					record.supplierEmail,
					record.date
				);

				const renderWoLinks = (workOrders: any[]) =>
					workOrders.map((wo, index) => (
						<span key={wo.id}>
							{index > 0 && ', '}
							<Link
								to={'/supplier/workOrders/detail/' + wo.id + '/details'}
								target="_blank"
								className="bodyregularunderline text-white"
							>
								WO #{wo.id}
							</Link>
						</span>
					));

				const content = (
					<div className="flex flex-col gap-xs">
						{notStarted.length > 0 && (
							<div className="bodyregular text-white">Not Started: {renderWoLinks(notStarted)}</div>
						)}
						{started.length > 0 && (
							<div className="bodyregular text-white">Started: {renderWoLinks(started)}</div>
						)}
						{ended.length > 0 && (
							<div className="bodyregular text-white">Ended: {renderWoLinks(ended)}</div>
						)}
					</div>
				);

				return (
					<Tooltip title={content} trigger="hover" placement="top">
						<span className="cursor-pointer underline">{label}</span>
					</Tooltip>
				);
			},
		},
		{
			title: 'Drive Time',
			key: 'driveTime',
			width: '14%',
			render: (_: any, record: FlattenedRow) => {
				return formatDuration(record.driveTimeMs);
			},
		},
		{
			title: 'Work Time',
			key: 'workTime',
			width: '14%',
			render: (_: any, record: FlattenedRow) => {
				return formatDuration(record.workTimeMs);
			},
		},
		{
			title: '',
			key: 'actions',
			width: '20%',
			render: (_: any, record: FlattenedRow) => (
				<Button
					onClick={() => handleViewDetails(record.supplierEmail, record.date, record.locationId)}
				>
					View Details <ExportOutlined />
				</Button>
			),
		},
	];

	if (loading) {
		return (
			<div className="flex justify-center p-l">
				<Spin />
			</div>
		);
	}

	if (groups.length === 0) {
		return (
			<div className="p-l">
				<Empty description="No work logs found for the selected date range" />
			</div>
		);
	}

	return (
		<div>
			{groups.map((group) => (
				<div key={group.date} className="mb-m">
					<div className="border-b border-grey-4 bg-grey-2 p-m">
						<span className="bodymedium text-character-primary">
							{moment(group.date).format('ddd, MMMM D')}
						</span>
					</div>
					<Table
						className="wrenchModeLogsTable wrenchModeLogsSectionTable"
						columns={buildColumns(group.techSpanMap)}
						dataSource={group.rows}
						pagination={false}
						size="middle"
						rowKey="key"
					/>
				</div>
			))}
			{loadingMore && (
				<div className="flex justify-center p-m">
					<Spin />
				</div>
			)}
			{hasMore && !loadingMore && (
				<div className="p-m">
					<Button onClick={onLoadMore} block>
						Load More
					</Button>
				</div>
			)}
		</div>
	);
};

export default withRouter<any, any>(WrenchModeLogsTable as any);
